import { routeFamily, type RouteFamily } from "@shared/telemetry/route-family";

export const NAVIGATION_TIMING_EVENT = "app_navigation_timing";
export const CONTENT_READY_TIMEOUT_MS = 15_000;

const CONTENT_ROOT_SELECTOR = "#main-content";
const PENDING_CONTENT_SELECTOR = '[data-slot="skeleton"], [aria-busy="true"]';
const MEASURED_FAMILIES: ReadonlySet<RouteFamily> = new Set(["admin", "dashboard"]);
const ID_SEGMENT = /^(?:[a-z0-9]{20,}|\d+|[A-Z]{1,4}-?\d[\w-]*)$/;

type NavigationType = "push" | "traverse";
type NavigationOutcome = "painted" | "timeout";

export interface NavigationTimingProperties {
  from_route: string;
  to_route: string;
  to_family: RouteFamily;
  navigation_type: NavigationType;
  duration_ms: number;
  outcome: NavigationOutcome;
}

interface PendingNavigation {
  fromPath: string;
  toPath: string;
  navigationType: NavigationType;
  startedAt: number;
}

export function routePattern(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
}

function isMeasuredPath(pathname: string): boolean {
  return MEASURED_FAMILIES.has(routeFamily(pathname));
}

function isContentReady(navigation: PendingNavigation): boolean {
  if (window.location.pathname === navigation.fromPath) return false;
  const root = document.querySelector(CONTENT_ROOT_SELECTOR);
  return root !== null && root.querySelector(PENDING_CONTENT_SELECTOR) === null;
}

function linkDestination(event: MouseEvent): URL | null {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }
  const target = event.target instanceof Element ? event.target : null;
  const anchor = target?.closest("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.hasAttribute("download")) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  const url = new URL(anchor.href, window.location.href);
  return url.origin === window.location.origin ? url : null;
}

function touchesContentRoot(records: MutationRecord[]): boolean {
  const root = document.querySelector(CONTENT_ROOT_SELECTOR);
  return root !== null && records.some((record) => root.contains(record.target));
}

function afterNextPaint(callback: () => void): () => void {
  let settle = 0;
  const frame = window.requestAnimationFrame(() => {
    settle = window.setTimeout(callback, 0);
  });
  return () => {
    window.cancelAnimationFrame(frame);
    window.clearTimeout(settle);
  };
}

function watchUntilReady(
  navigation: PendingNavigation,
  timeoutMs: number,
  report: (outcome: NavigationOutcome) => void,
): () => void {
  let contentChanged = false;
  let cancelConfirm = () => {};
  const timeout = window.setTimeout(() => finish("timeout"), timeoutMs);
  const observer = new MutationObserver((records) => {
    contentChanged ||= touchesContentRoot(records);
    cancelConfirm();
    if (!contentChanged || !isContentReady(navigation)) return;
    cancelConfirm = afterNextPaint(() => {
      if (isContentReady(navigation)) finish("painted");
    });
  });

  function stop() {
    observer.disconnect();
    cancelConfirm();
    window.clearTimeout(timeout);
    document.removeEventListener("visibilitychange", stopWhenHidden);
  }
  function stopWhenHidden() {
    if (document.visibilityState !== "visible") stop();
  }
  function finish(outcome: NavigationOutcome) {
    stop();
    report(outcome);
  }

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-busy", "data-slot"],
  });
  document.addEventListener("visibilitychange", stopWhenHidden);
  return stop;
}

function timingProperties(
  navigation: PendingNavigation,
  outcome: NavigationOutcome,
): NavigationTimingProperties {
  const destination = outcome === "painted" ? window.location.pathname : navigation.toPath;
  return {
    from_route: routePattern(navigation.fromPath),
    to_route: routePattern(destination),
    to_family: routeFamily(destination),
    navigation_type: navigation.navigationType,
    duration_ms: Math.round(performance.now() - navigation.startedAt),
    outcome,
  };
}

function isMeasurable(navigation: PendingNavigation): boolean {
  return (
    navigation.toPath !== navigation.fromPath &&
    isMeasuredPath(navigation.toPath) &&
    document.visibilityState === "visible"
  );
}

export function installNavigationTimer(
  capture: (properties: NavigationTimingProperties) => void,
  timeoutMs: number = CONTENT_READY_TIMEOUT_MS,
): () => void {
  let cancelPending: (() => void) | null = null;
  let lastPath = window.location.pathname;

  function start(navigation: PendingNavigation) {
    cancelPending?.();
    cancelPending = null;
    if (!isMeasurable(navigation)) return;
    cancelPending = watchUntilReady(navigation, timeoutMs, (outcome) => {
      cancelPending = null;
      lastPath = window.location.pathname;
      const properties = timingProperties(navigation, outcome);
      performance.measure(`${NAVIGATION_TIMING_EVENT} ${properties.to_route}`, {
        start: navigation.startedAt,
        detail: properties,
      });
      capture(properties);
    });
  }

  function onClick(event: MouseEvent) {
    const url = linkDestination(event);
    if (!url) return;
    lastPath = window.location.pathname;
    start({ fromPath: lastPath, toPath: url.pathname, navigationType: "push", startedAt: event.timeStamp });
  }

  function onPopState() {
    const fromPath = lastPath;
    lastPath = window.location.pathname;
    start({ fromPath, toPath: lastPath, navigationType: "traverse", startedAt: performance.now() });
  }

  document.addEventListener("click", onClick, true);
  window.addEventListener("popstate", onPopState);
  return () => {
    cancelPending?.();
    document.removeEventListener("click", onClick, true);
    window.removeEventListener("popstate", onPopState);
  };
}
